import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { useProductDetailsContext } from '../../../use-product-details-context';
import { DRAWER_TABS_V3 } from '../../drawer-tabs-v3';
import { messages } from '../../../messages';
import Global from '../../../../../store/global-selectors';
import { useIsUnitsOfMeasureAvailable } from '../../../../../feature-gates/use-is-units-of-measure-available';
import { useEnableSuppliersGrid } from '../../../../../feature-flags/hooks/use-enable-suppliers-grid';
import style from './drawer-sidebar.module.less';

export const ProductDetailsDrawerSidebar = () => {
  const intl = useIntl();

  const { productName, productSku, imageUrl, selectedTab, setSelectedTab, isSmartRoutingEnabled } = useProductDetailsContext();

  const isSysAdmin = useSelector(Global.selectUserisSysAdmin);
  const { isLdEnabled: isUnitsOfMeasureLdEnabled } = useIsUnitsOfMeasureAvailable();
  const isProductSuppliersTabEnabled = useEnableSuppliersGrid();

  const accessibleTabs = useMemo(
    () =>
      DRAWER_TABS_V3.filter((tab) =>
        tab.shouldRender({ isSysAdmin }, { isSmartRoutingEnabled, isUnitsOfMeasureLdEnabled, isProductSuppliersTabEnabled }),
      ),
    [isSysAdmin, isSmartRoutingEnabled, isUnitsOfMeasureLdEnabled, isProductSuppliersTabEnabled],
  );

  const handleTabClick = useCallback((tabId: string) => setSelectedTab(tabId), [setSelectedTab]);

  const activeTabId = selectedTab ?? DRAWER_TABS_V3[0].id;

  const skuLabel = `${intl.formatMessage(messages.productSku)}: ${productSku}`;

  return (
    <FlexColumn className={style.sidebar}>
      <FlexColumn className={style['product-info']} gap={Gap.Space8}>
        {imageUrl ? (
          <img alt={productName} className={style['product-image']} src={imageUrl} />
        ) : (
          <div className={style['product-image-placeholder']} />
        )}
        <Text className={style['product-name']} textType={TextType.Paragraph} value={productName} />
        <Text className={style['product-sku']} textType={TextType.Caption} value={skuLabel} />
      </FlexColumn>
      <FlexColumn className={style.nav} gap={Gap.Space16}>
        {accessibleTabs.map((tab) => (
          <Button
            key={tab.id}
            className={activeTabId === tab.id ? style['active-nav-item'] : style['nav-item']}
            disabled={activeTabId === tab.id}
            onClick={() => handleTabClick(tab.id)}
            theme={THEME_CONSTANTS.unstyled}
          >
            {tab.name()}
          </Button>
        ))}
      </FlexColumn>
    </FlexColumn>
  );
};
